import Backbone from "backbone";
import _ from "underscore";
import { CancelablePromise as Promise } from "./utils/cancelablePromise";
import AppView from "./app.view";
import { dispatcher, DispatcherEvents } from "./utils/dispatcher";
import { MobileActions } from "./store/mobile/actions";
import { IMobilePageState } from "./store/mobile/types";
import { isEqual, extend, empty } from "./utils/vanilla";
import { Unsubscribe } from "./lib/redux";
import Translator from "./utils/translator";
import { getTranslatedString, translatedStringExists, UntranslatedLogging, TranslationSource } from "./utils/helpers";
import * as logger from "./utils/logger";
import { DriveStore } from "./store/reducers";

export type TDriveViewConstructor<T extends Backbone.Model> = new (options: IDriveViewOptions<T> | undefined, pageMeta?: IMobilePageState) => DriveView<T>;

export interface IDriveViewOptions<T extends Backbone.Model> extends Backbone.ViewOptions<T> {
    app: AppView;
    state?: any;
    el?: string | HTMLElement;
}

export default class DriveView<T extends Backbone.Model> extends Backbone.NativeView<T> {
    onBeforeUnload: (() => Promise) | undefined;
    protected app: AppView;
    protected strings: string[] = [];
    protected isScrollBtnEnabled = false;
    private readonly translatedStrings: { [key: string]: string } = {};
    private displayPromise: Promise | undefined;
    private translatePromise: Promise | undefined;
    private readonly untranslated: Set<string>  = new Set();

    constructor(readonly options: IDriveViewOptions<T>, pageMeta?: IMobilePageState) {
        super(options);
        this.app = options.app;

        DriveView.commonWorkWithReactViews(this.app.store, pageMeta);
    }

    get translator() {
        if (this.app.translationService){
            let translator = this.app.translationService.getTranslator();
            if (translator) {
                return translator;
            }
        }
        return new Translator(this.getPreferredLanguage(), this.app.api.driveApi, this.app.store, this.app.featureFlagService);
    }

    get showScrollBtn() {
        return this.isScrollBtnEnabled;
    }

    static commonWorkWithReactViews(store: DriveStore, pageMeta?: IMobilePageState) {
        //TODO: In future always dispatch a MobileAction to modify pageState. Reducer should be responsible
        // for checking if the state changed.
        let statePageMeta = store.getState().mobile.pageState;
        if (pageMeta && !isEqual(statePageMeta, extend({}, statePageMeta, pageMeta))) {
            store.dispatch(MobileActions.setPageState(pageMeta));
        }

        let contentContainer = document.getElementById("root");
        contentContainer && (contentContainer.scrollTop = 0);
    }

    translate(language?: string): Promise<void> {
        if (!this.strings.length){
            return Promise.resolve();
        }
        this.translatePromise = this.translator.translate(this.strings, language).then(strings => {
            this.strings.forEach((str, index) => {
                this.translatedStrings[str] = strings[index];
            });
        });
        return this.translatePromise;
    }

    getString(id: string, language?: string, returnEmptyStringWhenMissing = false): string {
        this.logUntranslatedString(id);
        return getTranslatedString(id, this.translatedStrings, language || this.app?.translationService?.getLanguage(), returnEmptyStringWhenMissing);
    }

    display(templateStr: string, language?: string, el?: HTMLElement): Promise {
        let target: HTMLElement = (el || this.el);

        if (target && this.translator) {
            this.displayPromise = this.translator.translateTemplate(templateStr, language)
                .then(tmp => {
                    empty(target);
                    let template = document.createDocumentFragment();
                    [].forEach.call(tmp, item => {
                        template.appendChild(item);
                    });
                    target.appendChild(template);

                    return templateStr;
                }, () => {
                    // If we get lost in translation, just render *something*
                    target.innerHTML = templateStr;
                });
        } else {
            this.displayPromise = Promise.resolve(false);
        }
        return this.displayPromise;
    }

    delegate(eventName: string, selector: string, listener: Function): DriveView<T> {
        let wrappedListener = _.bind(function (evt: any) {
            dispatcher.trigger(DispatcherEvents.DriveAppInteraction, evt);
            listener.apply(this, arguments);
        }, this);
        let skipWrapped = eventName && eventName.indexOf("touchend") !== -1;
        return super.delegate(eventName, selector, skipWrapped ? listener : wrappedListener) as DriveView<T>;
    }

    unsubscribeFromStoreEventsAndCancelPromises() {
        this.storeUnsubscribe && this.storeUnsubscribe();
        this.translatePromise && this.translatePromise.cancel();
        this.displayPromise && this.displayPromise.cancel();
    }

    remove() {
        this.unsubscribeFromStoreEventsAndCancelPromises();
        super.remove();
        return this;
    }

    protected storeUnsubscribe: Unsubscribe = () => { };

    private logUntranslatedString (id: string){
        let store = this.app.store.getState();
        if (this.strings.indexOf(id) !== -1) {
            return;
        }
        if (store && !translatedStringExists(id, this.translatedStrings) && store.mobile.online && !this.untranslated.has(id)) {
            this.untranslated.add(id);
            let string: UntranslatedLogging = {
                untranslatedString: id,
                page: store.mobile.currentPage,
                pageState: store.mobile.pageState,
                version: store.data.clientVersion,
                source: TranslationSource.HELPERS
            };
            logger.log(logger.Source.UNTRANSLATED_STRING, `Untranslated String: ${ JSON.stringify(string) }`);
        }
    }

    private getPreferredLanguage() {
        return this.app.translationService ? this.app.translationService.getLanguage() : "en";
    }
}